import { BaseChart } from './BaseChart.js';

export class PieChart extends BaseChart {
    constructor({
        // DATOS
        /**
         * Array de objetos con los sectores del pie.
         * Cada objeto debe tener:
         * - value: número (requerido)
         * - name: string (requerido)
         * - itemStyle: object (opcional)
         */
        data = [],

        // GEOMETRÍA DEL PIE
        pieRadius = '50%',                  // Radio del pie o donut ('50%', 150, ['40%', '70%'])
        pieCenter = ['50%', '50%'],         // Centro del gráfico [x, y]
        startAngle = 90,                    // Ángulo inicial en grados (90 = arriba)
        minAngle = 0,                       // Ángulo mínimo por sector
        roseType = null,                    // 'radius', 'area' o null (pie estándar)

        // ETIQUETAS
        showLabels = true,                  // Mostrar etiquetas
        labelPosition = 'outside',          // 'outside', 'inside' o 'center'
        labelFontSize = 10, // Puedes forzar un default más pequeño para Pies aquí
        /**
         * Formato de etiquetas:
         * - función: (params) => `${params.name}: ${params.percent}%`
         * - plantilla: '{b}: {d}%'
         * - acepta html
         */
        labelFormatter = null,
        showPercentage = true,              // Mostrar porcentaje en etiquetas
        percentPrecision = 1,               // Decimales en porcentaje

        // LÍNEAS DE ETIQUETAS
        showLabelLines = true,              // Mostrar líneas conectoras
        labelLineLength = 15,               // Longitud del primer segmento
        labelLineLength2 = 15,              // Longitud del segundo segmento
        avoidLabelOverlap = true,           // Evitar solapamiento automático

        // BORDES DE SECTORES
        sectorBorderColor = '#fff',         // Color del borde
        sectorBorderWidth = 0,              // Grosor del borde
        sectorBorderRadius = 0,             // Radio de esquinas redondeadas

        // SELECCIÓN
        selectedMode = 'single',            // false, 'single' o 'multiple'
        selectedOffset = 10,                // Desplazamiento al seleccionar

        // COMPORTAMIENTO
        stillShowZeroSum = true,            // Mostrar incluso si suma = 0
        clickable = true,                   // Permitir clics en sectores

        // PROPIEDADES BASE
        ...baseProps
    } = {}) {
        // Constructor de la clase base con configuraciones específicas
        super({
            ...baseProps,
            // Sobrescribir defaults específicos para pie
            tooltipTrigger: baseProps.tooltipTrigger || 'item',
            showToolbox: baseProps.showToolbox !== undefined ? baseProps.showToolbox : true,
            toolboxFeatures: baseProps.toolboxFeatures || ['restore', 'saveAsImage'],
            animationDuration: baseProps.animationDuration || 1000,
            animationEasing: baseProps.animationEasing || 'cubicOut',
            showLabel: showLabels,
            labelPosition: labelPosition,
            labelFontSize: labelFontSize,
            showLegend: baseProps.showLegend !== undefined ? baseProps.showLegend : true,
            legendOrient: baseProps.legendOrient || 'vertical',
            legendLeft: baseProps.legendLeft || 'left',
            legendTop: baseProps.legendTop || 'middle',
        });

        // Propiedades específicas de PieChart
        this.data = data;
        this.pieRadius = pieRadius;
        this.pieCenter = pieCenter;
        this.startAngle = startAngle;
        this.minAngle = minAngle;
        this.roseType = roseType;
        this.labelFormatter = labelFormatter;
        this.showPercentage = showPercentage;
        this.percentPrecision = percentPrecision;
        this.showLabelLines = showLabelLines;
        this.labelLineLength = labelLineLength;
        this.labelLineLength2 = labelLineLength2;
        this.avoidLabelOverlap = avoidLabelOverlap;
        this.sectorBorderColor = sectorBorderColor;
        this.sectorBorderWidth = sectorBorderWidth;
        this.sectorBorderRadius = sectorBorderRadius;
        this.selectedMode = selectedMode;
        this.selectedOffset = selectedOffset;
        this.stillShowZeroSum = stillShowZeroSum;
        this.clickable = clickable;
    }

    /**
     * Calcula el total de todos los valores.
     * 
     * @returns {number} Suma total
     */
    calculateTotal() {
        return this.data.reduce((sum, item) => sum + (item.value || 0), 0);
    }

    /**
     * Prepara los datos añadiendo el porcentaje calculado.
     * 
     * @returns {Array} Datos con porcentaje
     */
    prepareData() {
        const total = this.calculateTotal();
        return this.data.map(item => ({
            ...item,
            percentage: total > 0 ? parseFloat(((item.value / total) * 100).toFixed(this.percentPrecision)) : 0
        }));
    }

    /**
     * Genera el tooltip por defecto con información detallada.
     * 
     * @param {Object} params - Parámetros del tooltip de ECharts
     * @returns {string} HTML formateado para el tooltip
     */
    getDefaultTooltipFormatter(params) {
        const percentage = params.percent.toFixed(this.percentPrecision);
        const total = this.calculateTotal();

        return `
            <div style="padding: 8px;">
                <strong style="font-size: 14px;">${params.name}</strong><br/>
                <div style="margin-top: 4px;">
                    <span style="color: #666;">Valor:</span> <strong>${params.value.toLocaleString()}</strong><br/>
                    <span style="color: #666;">Porcentaje:</span> <strong>${percentage}%</strong>
                </div>
                <div style="margin-top: 4px; padding-top: 4px; border-top: 1px solid #eee; font-size: 11px; color: #999;">
                    Total: ${total.toLocaleString()}
                </div>
            </div>
        `;
    }

    /**
     * Transformación: Donut chart (anillo)
     * 
     * @param {string} innerRadius - Radio interno (ej: '40%')
     * @param {string} outerRadius - Radio externo (ej: '70%')
     */
    transformToDonut(innerRadius = '40%', outerRadius = '70%') {
        this.pieRadius = [innerRadius, outerRadius];
        return this;
    }

    /**
     * Transformación: Rose/Nightingale chart
     * El radio de cada sector es proporcional a su valor
     * 
     * @param {string} type - 'radius' o 'area'
     */
    transformToRose(type = 'radius') {
        this.roseType = type;
        this.pieRadius = '65%';
        return this;
    }

    /**
     * Transformación: Semicírculo (gauge-like)
     * Muestra solo la mitad superior del círculo
     */
    transformToSemiCircle() {
        this.pieCenter = ['50%', '70%'];
        this.pieRadius = '100%';
        this.startAngle = 180;
        return this;
    }

    /**
     * Transformación: Etiquetas internas
     * Muestra etiquetas dentro de los sectores
     */
    transformToInnerLabels() {
        this.labelPosition = 'inside';
        this.showLabelLines = false;
        return this;
    }

    /**
     * Transformación: Sin etiquetas
     */
    transformToNoLabels() {
        this.showLabels = false;
        return this;
    }

    /**
     * Transformación: Estilo moderno con bordes redondeados
     */
    transformToModern() {
        this.sectorBorderRadius = 8;
        this.sectorBorderWidth = 3;
        this.sectorBorderColor = '#fff';
        return this;
    }

    /**
     * Transformación: Estilo minimalista
     */
    transformToMinimal() {
        this.sectorBorderWidth = 0;
        this.showLabelLines = false;
        this.selectedMode = false;
        return this;
    }

    /**
     * Transformación: Dashboard compacto
     */
    transformToCompact() {
        this.pieRadius = '60%';
        this.labelPosition = 'inside';
        this.showLabelLines = false;
        this.showPercentage = true;
        return this;
    }

    /**
     * Encuentra el sector con el valor más alto en los datos actuales.
     * 
     * Útil para resaltar automáticamente la porción principal del gráfico.
     * @returns {Object|null} El objeto del sector más grande
     */
    getLargestSector() {
        if (!this.data || this.data.length === 0) return null;

        return this.data.reduce((maxItem, currentItem) => {
            const maxValue = maxItem.value || 0;
            const currentValue = currentItem.value || 0;
            return currentValue > maxValue ? currentItem : maxItem;
        }, this.data[0]);
    }

    /**
     * Transformación: Explotar primer sector (destacar)
     * Desplaza el primer/mayor sector hacia afuera
     */
    transformToExploded() {
        if (this.data.length > 0) {
            // Encontrar el sector más grande
            const largest = this.getLargestSector();
            const index = this.data.findIndex(item => item.name === largest.name);

            // Modificar datos para explotar ese sector
            this.data = this.data.map((item, idx) => ({
                ...item,
                itemStyle: idx === index ? {
                    ...item.itemStyle,
                    shadowBlur: 20,
                    shadowColor: 'rgba(0, 0, 0, 0.3)'
                } : item.itemStyle,
                emphasis: idx === index ? {
                    scale: true,
                    scaleSize: 15
                } : undefined
            }));
        }
        this.selectedOffset = 20;
        return this;
    }

    getOptions() {
        const preparedData = this.prepareData();
        const baseLabelStyle = this.getLabelStyle();

        const options = {
            color: this.colorPalette,
            title: this.getTitleConfig(),
            tooltip: this.showTooltip
                ? this.getTooltipConfig(
                    this.tooltipFormatter || ((params) => this.getDefaultTooltipFormatter(params))
                )
                : {},
            series: [
                {
                    id: this.seriesId,
                    name: this.seriesName,
                    type: 'pie',
                    radius: this.pieRadius,
                    center: this.pieCenter,
                    startAngle: this.startAngle,
                    roseType: this.roseType,
                    data: preparedData,

                    // Selección
                    selectedMode: this.selectedMode,
                    selectedOffset: this.selectedOffset,

                    // Comportamiento
                    minAngle: this.minAngle,
                    avoidLabelOverlap: this.avoidLabelOverlap,
                    stillShowZeroSum: this.stillShowZeroSum,

                    // Estilos de sectores
                    itemStyle: {
                        borderColor: this.sectorBorderColor,
                        borderWidth: this.sectorBorderWidth,
                        borderRadius: this.sectorBorderRadius,
                    },

                    // Etiquetas
                    label: {
                        ...baseLabelStyle,
                        formatter: this.labelFormatter || ((params) => {
                            if (this.showPercentage) {
                                return `${params.name}\n${params.percent.toFixed(this.percentPrecision)}%`;
                            }
                            return `${params.name}\n${params.value.toLocaleString()}`;
                        }),
                    },

                    // Líneas de etiquetas
                    labelLine: {
                        show: this.showLabelLines && this.labelPosition === 'outside',
                        length: this.labelLineLength,
                        length2: this.labelLineLength2,
                    },

                    // Interactividad
                    emphasis: this.emphasis,
                    blur: this.blur,

                    // Animaciones
                    animationDuration: this.animationDuration,
                    animationEasing: this.animationEasing,
                    animationType: 'expansion',
                },
            ],
        };

        // Agregar leyenda si está habilitada
        if (this.showLegend) {
            const legendConfig = this.getLegendConfig();
            legendConfig.data = this.data.map(item => item.name);
            legendConfig.type = 'scroll'; // Scroll para muchos elementos
            options.legend = legendConfig;
        }

        // Agregar toolbox usando método de la clase base
        const toolbox = this.getToolboxConfig();
        if (toolbox.show) {
            options.toolbox = toolbox;
        }

        return options;
    }
}